import type {Callback1, ComponentPointers, Pointer, PS} from '@wix/document-services-types'
import _ from 'lodash'
import hooks from '../hooks/hooks'
import pathUtils from './utils/pathUtils'
import wixCodeLifecycleService from './services/wixCodeLifecycleService'
import userCodeCacheKillerService from './services/userCodeCacheKillerService'
import wixCodeMonitoring from './services/wixCodeMonitoringWrapper'
import fileSystemAPI from './services/fileSystemAPI'
import codePackageAPI from './services/codePackageAPI'
import pagesAPI from './services/pagesAPI'
import pageCodeDuplicator from './services/pageCodeDuplicator'
import codeIntelligenceService from './services/codeIntelligenceService'
import platform from '../platform/platform'
import wixCodeConstants from './utils/constants'
import clientSpecMap from '../siteMetadata/clientSpecMap'
import bi from '../bi/bi'
import biEvents from '../bi/events.json'
import wixCodeUserScriptsService from './utils/wixCodeUserScriptsService'
import * as santaCoreUtils from '@wix/santa-core-utils'
import generalInfo from '../siteMetadata/generalInfo'
import saveAPI from '../saveAPI/saveAPI'
import componentCode from '../component/componentCode'
import disabledWixCodeSave from './services/disabledWixCodeSave'
import wixCodeErrors from './utils/errors'
import {ReportableError} from '@wix/document-manager-utils'
import type {WixCodeFileStateChange} from '@wix/document-manager-extensions/src/extensions/distributor/messages'
import type {DistributorExtensionAPI} from '@wix/document-manager-extensions/src/extensions/distributor/distributor'
import {contextAdapter} from '../utils/contextAdapter'
import experiment from 'experiment-amd'
import {_tempGetAppAppDef} from './utils/tempGetAppDefId'
import cms from '../cms/cms'

function getClientSpec(ps: PS) {
    let wixCodeApp
    if (experiment.isOpen('dm_cloudSiteExtensionMigrationDev')) {
        wixCodeApp = clientSpecMap.getAppDataByAppDefinitionId(ps, wixCodeConstants.WIX_CODE_APP_DEF_ID)
    }
    contextAdapter.utils.fedopsLogger.interactionStarted('wixCodeApp', {extras: {useNewApp: !!wixCodeApp}})
    const actualWixCodeAppInstance =
        wixCodeApp ?? _.head(clientSpecMap.filterAppsDataByType(ps, wixCodeConstants.WIX_CODE_SPEC_TYPE))
    contextAdapter.utils.fedopsLogger.interactionEnded('wixCodeApp')
    return actualWixCodeAppInstance
}

function reportEditorLoadedWithApps(ps: PS) {
    const appNames = _(clientSpecMap.getAppsDataWithPredicate(ps, csm => _.filter(csm, 'platformApp')))
        .map('displayName')
        .value()

    if (!_.isEmpty(appNames)) {
        bi.event(ps, biEvents.EDITOR_PLATFORM_APPS_LOADED, {app_list: appNames, number_of_apps: appNames.length})
    }
}

const debouncedFlush = _.debounce(fileSystemAPI.flush, 1000)
const onFileOrFolderChanged = (ps: PS) => {
    if (!ps.runtimeConfig.disableWixCodeContinuousSave) {
        debouncedFlush(ps, {origin: fileSystemAPI.FLUSH_ORIGINS.COUNTINUOUS_SAVE})
    }
}

const reportIfMissingOpenGridApp = (ps: PS) => {
    const isProvisioned = !!getClientSpec(ps)
    if (isProvisioned) {
        const lastSavedGridAppId = ps.extensionAPI.wixCode.getRevisionGridAppId()
        const openGridAppId = ps.extensionAPI.wixCode.getOpenGridAppId()
        if (_.isEmpty(openGridAppId) && !_.isEmpty(lastSavedGridAppId)) {
            ps.logger.captureError(
                new wixCodeErrors.WixCodeMissingOpenGridAppError({openGridAppId, lastSavedGridAppId}),
                {
                    extras: {
                        openGridAppId,
                        lastSavedGridAppId,
                        isTemplate: generalInfo.isTemplate(ps),
                        isDraft: generalInfo.isDraft(ps)
                    },
                    level: 'error'
                }
            )
        }
    }
}

function initializeWixCode(ps: PS) {
    if (!ps.runtimeConfig.supportsPlatformInitialization) {
        return
    }
    pathUtils.initPaths(ps)

    userCodeCacheKillerService.init(ps)

    if (wixCodeLifecycleService.isProvisioned(ps)) {
        performAfterProvisionActions(ps).catch(error => {
            ps.logger?.error?.('Failed to perform after provision actions during initialization', {
                error: error?.message || String(error)
            })
        })
        fileSystemAPI.prefetchUserCode(ps)
    }

    ps.extensionAPI.wixCode.syncGridAppToViewer()
    ps.extensionAPI.wixCode.generateWixCodeSessionId()

    reportIfMissingOpenGridApp(ps)
    reportEditorLoadedWithApps(ps)

    const {distributor} = ps.extensionAPI as DistributorExtensionAPI
    distributor.subscribeToMessage<WixCodeFileStateChange>('wixCodeFileStateChange', ({data: {changedPaths}}) => {
        if (!ps.dal.get(ps.pointers.wixCode.getIsolatedGridApp())) {
            return fileSystemAPI.handleExternalChange(ps, changedPaths)
        }
    })

    hooks.registerHook(hooks.HOOKS.WIX_CODE.FILE_OR_FOLDER_CHANGED, onFileOrFolderChanged)
    ps.extensionAPI.wixCode.updateWixCodeLocalProvisionState(wixCodeLifecycleService.isProvisioned(ps))
    ps.extensionAPI.wixCode.updateWixCodeModelLocalState(!!ps.extensionAPI.wixCode.getEditedGridAppId())
    ps.extensionAPI.wixCode.updateSiteExtensionState(!!getClientSpec(ps))
    ps.extensionAPI.wixCode.subscribeToWixCodeProvision(async () => {
        await performAfterProvisionActions(ps)
        reloadAppsContainer(ps)
    })
}

async function performAfterProvisionActions(ps: PS) {
    fileSystemAPI.handleSchemaInvalidationActions()
    // TODO: replace once platform apps provision flow has been decided
    if (wixCodeLifecycleService.isProvisioned(ps)) {
        cms.loadDataBinding(ps)
        _tempInitWixCodeEditorApp(ps)
    }
}

function reloadAppsContainer(ps: PS) {
    ps.siteAPI.reloadAppsContainer()
}

const saveSite = async (ps: PS): Promise<void> => {
    const isQA = ps.siteAPI.isQaMode()

    if (isQA) {
        return
    }
    await saveAPI.promises.save(ps, false)
}

async function initializeWixCodeWidget(ps: PS) {
    await performAfterProvisionActions(ps)
    reloadAppsContainer(ps)
}

async function provisionAsync(ps: PS) {
    if (!wixCodeLifecycleService.isProvisioned(ps)) {
        disabledWixCodeSave.ensureWixCodeSaveAllowed(ps)
    }
    const traceEnd = wixCodeMonitoring.trace(ps, {action: 'provision'})
    try {
        const provisionResult = await wixCodeLifecycleService.provision(ps)
        componentCode.generateNicknamesForSite(ps, null, true)
        await initializeWixCodeWidget(ps)
        ps.extensionAPI.wixCode.updateWixCodeLocalProvisionState(true)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
        await saveSite(ps)
        traceEnd({message: provisionResult})
        return provisionResult
    } catch (error: any) {
        ps.extensionAPI.wixCode.updateWixCodeLocalProvisionState(false)
        ps.setOperationsQueue.asyncPreDataManipulationComplete(
            null,
            new ReportableError({
                errorType: 'WixCodeProvisionFailed',
                message: error.message
            })
        )
        traceEnd({level: wixCodeMonitoring.levels.ERROR, message: error})
        throw error
    }
}

function provision(ps: PS, callbacks: {onSuccess: Callback1<any>; onError: Callback1<any>}) {
    callbacks = _.defaults({}, callbacks, {onSuccess: _.noop, onError: _.noop})
    provisionAsync(ps).then(callbacks.onSuccess, callbacks.onError) // eslint-disable-line promise/prefer-await-to-then
}

function getFocusedRootRef(ps: PS, compPointers: ComponentPointers) {
    const currentRootId = ps.siteAPI.getCurrentUrlPageId()
    const viewModes = santaCoreUtils.constants.VIEW_MODES
    const viewMode = ps.siteAPI.isMobileView() ? viewModes.MOBILE : viewModes.DESKTOP
    return compPointers.getPage(currentRootId, viewMode)
}

function getWidgetRef(ps: PS, compRef: Pointer) {
    const compPointers = ps.pointers.components
    const compRootRef = compPointers.getPageOfComponent(compRef)
    if (!compRootRef) {
        return null
    }
    if (compPointers.isMasterPage(compRootRef)) {
        return getFocusedRootRef(ps, compPointers)
    }
    return compRootRef
}

function _tempInitWixCodeEditorApp(ps: PS) {
    // TODO: replace once platform apps provision flow has been decided
    platform.initApp(ps, _tempGetAppAppDef(ps, '', 'wix-code-editor-app', 'wix-code', false))
}

function duplicatePageCode(ps: PS, newPageId: string, originalPageId: string) {
    if (wixCodeLifecycleService.isProvisioned(ps)) {
        pageCodeDuplicator.duplicatePageCode(ps, newPageId, originalPageId)
    }
}

hooks.registerHook(hooks.HOOKS.PLATFORM.APP_PROVISIONED, reloadAppsContainer)
hooks.registerHook(hooks.HOOKS.PLATFORM.APP_UPDATED, reloadAppsContainer)

function isUserCodeUrl(ps: PS, url: string) {
    const wixCodeSpec = getClientSpec(ps)
    return wixCodeUserScriptsService.isUserCodeUrl(ps, url, wixCodeSpec)
}

function getSourceMapUrl(ps: PS, userCodeUrl: string) {
    const gridAppId = ps.extensionAPI.wixCode.getEditedGridAppId()
    const wixCodeSpec = getClientSpec(ps)
    return wixCodeUserScriptsService.getSourceMapUrl(ps, userCodeUrl, gridAppId, wixCodeSpec)
}

const setIsolatedGridApp = (ps: PS, gridAppId: string) => {
    ps.extensionAPI.wixCode.setIsolatedGridAppId(gridAppId)
}

const getIsolatedGridApp = (ps: PS) => ps.extensionAPI.wixCode.getIsolatedGridAppId()
const getEditedGridApp = (ps: PS) => ps.extensionAPI.wixCode.getEditedGridAppId()
const getWixCodeSessionId = (ps: PS) => ps.extensionAPI.wixCode.getWixCodeSessionId()

export default {
    initializeWixCode,
    provision,
    provisionAsync,
    isProvisioned: wixCodeLifecycleService.isProvisioned,
    getClientSpec,
    getWidgetRef,
    duplicatePageCode,
    setIsolatedGridApp,
    getIsolatedGridApp,
    getWixCodeSessionId,
    getEditedGridApp,
    getCompSdkType: codeIntelligenceService.getCompSdkType,
    log: {
        levels: wixCodeMonitoring.levels,
        trace: wixCodeMonitoring.trace
    },
    codeIntelligence: codeIntelligenceService,
    fileSystem: fileSystemAPI,
    codePackages: codePackageAPI,
    pages: pagesAPI,
    userScripts: {
        isUserCodeUrl,
        getSourceMapUrl
    }
}
